<script setup lang="ts">
/**
 * Avatar-Menü oben rechts: der Kreis mit den Initialen, ein Klick öffnet die
 * persönlichen Aktionen — Benutzereinstellungen und Abmelden.
 *
 * Zuvor standen Name, Rolle und ein Abmelden-Knopf offen in der Kopfleiste. Das ist
 * sauber, wächst aber schlecht: Jede weitere persönliche Aktion (Konto, Design,
 * später vielleicht Sprache) müsste sich denselben knappen Platz teilen. Ein Menü
 * hinter dem Avatar ist der übliche, ruhige Ort dafür.
 *
 * Klick-außerhalb und Escape schließen — dasselbe Verhalten wie beim
 * Benachrichtigungs-Panel, damit sich die beiden Menüs in der Kopfleiste gleich
 * anfühlen.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ROLE_LABELS } from '@pmpro/shared';
import Icon from './Icon.vue';
import UserAvatar from './UserAvatar.vue';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toasts';

const auth = useAuthStore();
const toasts = useToastStore();
const router = useRouter();

const offen = ref(false);
const wurzel = ref<HTMLElement | null>(null);

onMounted(() => {
  document.addEventListener('click', klickAusserhalb);
  document.addEventListener('keydown', taste);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', klickAusserhalb);
  document.removeEventListener('keydown', taste);
});

function klickAusserhalb(event: MouseEvent): void {
  if (!offen.value) return;
  if (wurzel.value && !wurzel.value.contains(event.target as Node)) offen.value = false;
}

function taste(event: KeyboardEvent): void {
  if (event.key === 'Escape') offen.value = false;
}

async function zuEinstellungen(): Promise<void> {
  offen.value = false;
  await router.push({ name: 'konto' });
}

async function abmelden(): Promise<void> {
  offen.value = false;
  try {
    await auth.logout();
  } catch {
    toasts.fehler('Abmelden fehlgeschlagen — die Sitzung wurde lokal beendet.');
  }
  await router.push({ name: 'login' });
}
</script>

<template>
  <div ref="wurzel" class="menu">
    <button
      type="button"
      class="menu__knopf"
      :aria-expanded="offen"
      aria-haspopup="true"
      :aria-label="`Konto von ${auth.user?.name ?? ''}`"
      @click="offen = !offen"
    >
      <UserAvatar :name="auth.user?.name ?? ''" :schluessel="auth.user?.id" :size="32" />
      <Icon
        name="aufklappen"
        :size="15"
        class="menu__pfeil"
        :class="{ 'menu__pfeil--auf': offen }"
      />
    </button>

    <div v-if="offen" class="panel" role="menu">
      <div class="panel__kopf">
        <UserAvatar :name="auth.user?.name ?? ''" :schluessel="auth.user?.id" :size="40" />
        <div class="panel__wer">
          <span class="panel__name">{{ auth.user?.name }}</span>
          <span class="panel__mail muted">{{ auth.user?.email }}</span>
          <span v-if="auth.user" class="panel__rolle">{{ ROLE_LABELS[auth.user.role] }}</span>
        </div>
      </div>

      <hr class="panel__trenner" />

      <button type="button" class="eintrag" role="menuitem" @click="zuEinstellungen">
        <Icon name="einstellungen" :size="16" />
        Benutzereinstellungen
      </button>
      <button type="button" class="eintrag" role="menuitem" @click="abmelden">
        <Icon name="abmelden" :size="16" />
        Abmelden
      </button>
    </div>
  </div>
</template>

<style scoped>
.menu {
  position: relative;
}

.menu__knopf {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem;
  border: none;
  border-radius: var(--radius-pill);
  background: transparent;
  transition: background var(--t-fast);
}
.menu__knopf:hover {
  background: var(--color-surface-2);
}

.menu__pfeil {
  color: var(--color-muted);
  transition: transform var(--t-fast);
}
.menu__pfeil--auf {
  transform: rotate(180deg);
}

.panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 20;
  width: 15rem;
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.panel__kopf {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.5rem 0.5rem;
}

.panel__wer {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.05rem;
}

.panel__name {
  font-weight: 600;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel__mail {
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel__rolle {
  margin-top: 0.15rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-primary);
}

.panel__trenner {
  margin: 0.35rem 0;
  border: none;
  border-top: 1px solid var(--color-border);
}

.eintrag {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-radius: var(--radius);
  background: transparent;
  color: var(--color-text);
  text-align: left;
  font-size: 0.8125rem;
}
.eintrag:hover {
  background: var(--color-surface-2);
}
</style>
